/**
 * Nonce and JTI utilities for replay attack prevention.
 *
 * SECURITY ARCHITECTURE:
 * The JTI cache lives in-process (per-tab). This means:
 *   - A replay within the same tab is caught.
 *   - A replay from a second tab is NOT caught client-side.
 *
 * The authoritative replay store MUST be server-side. The client-side cache
 * provides a fast first line of defence and reduces server load.
 *
 * For multi-tab coordination, see BroadcastChannel or SharedWorker patterns.
 */

/**
 * Generate a cryptographically random base64url string.
 * Uses Web Crypto API — never Math.random().
 */
export function generateRandomBytes(byteLength: number): Uint8Array {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export function generateNonce(byteLength = 16): string {
  return bytesToBase64Url(generateRandomBytes(byteLength));
}

/** 192-bit JTI — sufficiently large to prevent brute-force collision. */
export function generateJti(): string {
  return generateNonce(24);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// ─── JTI cache ────────────────────────────────────────────────────────────

interface JtiRecord {
  readonly usedAt: number;
  readonly expiresAt: number;
}

/**
 * Time-bounded sliding-window JTI cache.
 *
 * PERFORMANCE: Uses a Map for O(1) lookups. Eviction is lazy (on each check)
 * plus periodic via setInterval. Memory use is bounded by windowSeconds ×
 * request rate. At 100 req/s with a 300s window, that's ~30k entries —
 * acceptable. If your rate is higher, use a server-side Redis set instead.
 */
export interface JtiCacheOptions {
  /** Auto-start the eviction timer in the constructor. Defaults to true. */
  autoCleanup?: boolean;
  /** Interval between cleanup sweeps when autoCleanup is on. Defaults to 60s. */
  cleanupIntervalMs?: number;
}

export class JtiCache {
  private readonly cache = new Map<string, JtiRecord>();
  private readonly windowMs: number;
  private cleanupHandle: ReturnType<typeof setInterval> | null = null;

  constructor(windowSeconds: number, opts: JtiCacheOptions = {}) {
    this.windowMs = windowSeconds * 1_000;
    if (opts.autoCleanup !== false) {
      this.startAutoCleanup(opts.cleanupIntervalMs ?? 60_000);
    }
  }

  /**
   * Start automatic eviction of expired JTIs.
   * Called automatically by the constructor unless `autoCleanup: false` is
   * passed. Idempotent. Call `destroy()` when the SDK is torn down.
   */
  startAutoCleanup(intervalMs = 60_000): void {
    if (this.cleanupHandle !== null) return;
    this.cleanupHandle = setInterval(() => this.evictExpired(), intervalMs);
    // Unref the timer if available (Node.js) so it doesn't block process exit.
    if (typeof this.cleanupHandle === 'object' && 'unref' in this.cleanupHandle) {
      (this.cleanupHandle as unknown as { unref(): void }).unref();
    }
  }

  stopAutoCleanup(): void {
    if (this.cleanupHandle !== null) {
      clearInterval(this.cleanupHandle);
      this.cleanupHandle = null;
    }
  }

  /**
   * Returns `true` if the JTI is being seen for the first time (and records it).
   * Returns `false` if the JTI has already been used — replay detected.
   *
   * @param jti         The JWT ID claim value.
   * @param tokenExpSec The JWT `exp` claim in Unix epoch seconds (optional but
   *                    strongly recommended — tightens the eviction window).
   */
  checkAndRecord(jti: string, tokenExpSec?: number): boolean {
    this.evictExpired();

    if (this.cache.has(jti)) {
      return false;
    }

    const now = Date.now();
    const expiresAt = tokenExpSec !== undefined
      ? Math.min(tokenExpSec * 1_000, now + this.windowMs)
      : now + this.windowMs;

    this.cache.set(jti, { usedAt: now, expiresAt });
    return true;
  }

  /** Visible for testing. */
  get size(): number { return this.cache.size; }

  destroy(): void {
    this.stopAutoCleanup();
    this.cache.clear();
  }

  private evictExpired(): void {
    const now = Date.now();
    for (const [jti, record] of this.cache) {
      if (record.expiresAt <= now) this.cache.delete(jti);
    }
  }
}
